/**
 * Formato dos números, em pt-BR. Porte direto do protótipo.
 *
 * REGRA DO PAINEL: tudo em reais, NENHUM número em percentual na tela. A
 * proporção existe só para posicionar a agulha do velocímetro e escolher a cor
 * da faixa — e nunca vira texto. Por isso não há nenhuma função de percentual
 * aqui, e não é esquecimento.
 */

/* `Intl.NumberFormat` é instanciado UMA vez. Criar o formatador a cada
   repintura custa caro, e esta tela repinta a cada pedido que entra, por
   4 dias seguidos. */
const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })
const UMA_CASA = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
/* Três casas em milhões = precisão de milhar: `2,030 mi` são R$ 2.030.000, e o
   milhar continua na tela (ver `valorCurto`). */
const TRES_CASAS = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
})
const HORA = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})
const DATA_CURTA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
})

/** `R$ 83.460.000` — o número cheio, para o herói do topo e para o feed. */
export function moeda(valor: number): string {
  if (!Number.isFinite(valor)) return 'R$ —'
  return 'R$ ' + INTEIRO.format(valor)
}

/** `R$ 6,5 mi` — para card e subitem, onde o número cheio não cabe. */
export function milhoes(valor: number): string {
  if (!Number.isFinite(valor)) return 'R$ —'
  return 'R$ ' + UMA_CASA.format(valor / 1e6) + ' mi'
}

/**
 * O VENDIDO: sempre com precisão de MILHAR, mas na unidade que a sala lê.
 *
 * `milhoes()` continua valendo para a META, que é cadastrada, redonda e não
 * muda durante o evento. Para o vendido, a escala precisa resolver dois
 * problemas de leitura ao mesmo tempo, os dois vistos no telão:
 *
 *   - 13/10, de manhã: `R$ 0,0 mi` para os R$ 37.385 da Alakazoo, indistinguível
 *     de divisão que não vendeu nada. Arredondar acima do milhar apaga
 *     informação justo na hora em que a sala está olhando.
 *   - 13/10, à tarde: `R$ 1,4 mi` para a Lez — não havia como saber se eram
 *     1.401 ou 1.449 mil, uma diferença de R$ 48 mil.
 *   - 14/10: com a escala única em milhares, a Lez em R$ 2.030.000 saía como
 *     `R$ 2.030 mil` e a sala lia "dois mil e trinta". Passado o milhão, a
 *     unidade `mil` deixa de evidenciar a ordem de grandeza.
 *
 * Daí os três degraus — e os `mi` com TRÊS casas, que é o mesmo que precisão de
 * milhar (`2,030 mi` são 2 milhões e 30 mil, não 2,03 arredondado):
 *
 *   R$ 0            zero é zero, não `R$ 0,0 mil`
 *   R$ 830          abaixo de mil, o valor cheio — não existe `R$ 0,8 mil`
 *   R$ 9 mil        a Vila Flor num dia fraco
 *   R$ 2,030 mi     a Lez em 14/10 — antes `R$ 2.030 mil`
 *   R$ 28 mi        milhão redondo não leva casa nenhuma
 *
 * O degrau é escolhido pelo valor JÁ arredondado ao milhar, não pelo valor
 * cheio: R$ 999.600 arredonda para 1.000 mil, e `R$ 1.000 mil` seria "um milhão
 * escrito do jeito errado" — sai `R$ 1 mi`. Negativo é possível (devolução) e o
 * sinal vem na frente do `R$`.
 */
export function valorCurto(valor: number): string {
  if (!Number.isFinite(valor)) return 'R$ —'
  const sinal = valor < 0 ? '-' : ''
  const absoluto = Math.abs(valor)
  if (absoluto < 1_000) return sinal + 'R$ ' + INTEIRO.format(absoluto)
  const milhares = Math.round(absoluto / 1e3)
  if (milhares < 1_000) return sinal + 'R$ ' + INTEIRO.format(milhares) + ' mil'
  /* Dividir `milhares` (e não `absoluto`) mantém o degrau e o texto de acordo:
     o número formatado é exatamente o milhar que o degrau avaliou. */
  const emMilhoes = milhares / 1e3
  const formatado = milhares % 1_000 === 0 ? INTEIRO.format(emMilhoes) : TRES_CASAS.format(emMilhoes)
  return sinal + 'R$ ' + formatado + ' mi'
}

/** `09:42:03`, do relógio do cabeçalho. */
export function hora(quando: Date | number): string {
  return HORA.format(quando)
}

/**
 * `09/10/26`, das duas colunas de data do feed.
 *
 * A entrada é `YYYY-MM-DD` (o contrato do snapshot). Monta-se a data com
 * `new Date(ano, mês-1, dia)` de propósito: `new Date('2026-10-09')` é
 * interpretado como UTC, e no fuso de Brasília isso volta um dia — o pedido de
 * 09/10 apareceria como 08/10 no telão.
 */
export function dataCurta(iso: string | null | undefined): string {
  if (!iso) return '—'
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!partes) return '—'
  const [, ano, mes, dia] = partes
  return DATA_CURTA.format(new Date(Number(ano), Number(mes) - 1, Number(dia)))
}

/**
 * Largura do preenchimento da barra do subitem, em porcentagem do elemento.
 *
 * É geometria de CSS, não número exibido: a escala da barra é a mesma do
 * velocímetro (teto em 120 % da meta), para a barra cheia e a agulha no fim
 * significarem a mesma coisa.
 */
export function larguraBarra(proporcao: number): string {
  const p = Number.isFinite(proporcao) ? Math.max(proporcao, 0) : 0
  return (Math.min(p, 1.2) / 1.2) * 100 + '%'
}
